/**
 * Build a standings table from fixtures + teams: 3 pts win, 1 draw, 0 loss.
 */

export function buildStandingsFromFixtures(fixtures, allTeams) {
  const teamById = Object.fromEntries((allTeams || []).map((t) => [t.id, t]));
  const stats = new Map();

  const ensure = (teamId) => {
    if (!stats.has(teamId)) {
      stats.set(teamId, { w: 0, l: 0, d: 0, ff: 0, gf: 0, ga: 0 });
    }
    return stats.get(teamId);
  };

  for (const t of allTeams || []) {
    ensure(t.id);
  }

  for (const f of fixtures || []) {
    const hs = f.home_team_score;
    const as = f.away_team_score;
    const hi = f.home_team_id;
    const ai = f.away_team_id;
    if (hi == null || ai == null) continue;
    if (f.is_forfeit) {
      ensure(hi).ff += 1;
      ensure(ai).ff += 1;
      continue;
    }
    if (hs == null || as == null) continue;

    const h = ensure(hi);
    const a = ensure(ai);
    const homeScore = Number(hs);
    const awayScore = Number(as);
    h.gf += homeScore;
    h.ga += awayScore;
    a.gf += awayScore;
    a.ga += homeScore;

    if (homeScore > awayScore) {
      h.w += 1;
      a.l += 1;
    } else if (homeScore < awayScore) {
      h.l += 1;
      a.w += 1;
    } else {
      h.d += 1;
      a.d += 1;
    }
  }

  const knownIds = new Set((allTeams || []).map((t) => t.id));
  for (const f of fixtures || []) {
    if (f.home_team_id != null) knownIds.add(f.home_team_id);
    if (f.away_team_id != null) knownIds.add(f.away_team_id);
  }

  const rows = [];
  for (const id of knownIds) {
    const s = ensure(id);
    const t = teamById[id];
    const name = t?.name ?? `Team #${id}`;
    const w = s.w;
    const l = s.l;
    const d = s.d;
    const gf = s.gf;
    const ga = s.ga;
    const gp = w + l + d + s.ff;
    const points = w * 3 + d;
    const goalDifference = gf - ga;
    rows.push({
      id,
      name,
      wins: w,
      losses: l,
      draws: d,
      goalsFor: gf,
      goalsAgainst: ga,
      goalDifference,
      points,
      gp,
    });
  }

  rows.sort(compareStandingRows);

  return rows;
}

export const compareStandingRows = (a, b) =>
  b.points - a.points ||
  b.goalDifference - a.goalDifference ||
  b.goalsFor - a.goalsFor ||
  String(a.name).localeCompare(String(b.name));

const dayOf = (f) => String(f.date || "").slice(0, 10);
const isPlayed = (f) => f.is_forfeit === true || (f.home_team_score != null && f.away_team_score != null);

/**
 * Adds `movement` to each row of an already-sorted table: places gained (positive) or lost
 * (negative) since before the latest played matchday (one fixture date). `null` when there is
 * no earlier matchday to compare against.
 */
export function withMovement(rows, fixtures) {
  const ids = new Set((rows || []).map((r) => String(r.id)));
  const played = (fixtures || []).filter(
    (f) => isPlayed(f) && (ids.has(String(f.home_team_id)) || ids.has(String(f.away_team_id)))
  );
  const days = [...new Set(played.map(dayOf).filter(Boolean))].sort();
  if (days.length < 2) return (rows || []).map((r) => ({ ...r, movement: null }));

  const latest = days[days.length - 1];
  const before = new Map(
    rows.map((r) => [
      String(r.id),
      {
        id: r.id,
        name: r.name,
        points: Number(r.points) || 0,
        goalDifference: Number(r.goalDifference) || 0,
        goalsFor: Number(r.goalsFor) || 0,
      },
    ])
  );
  const undo = (teamId, scored, conceded) => {
    const r = before.get(String(teamId));
    if (!r) return;
    r.goalsFor -= scored;
    r.goalDifference -= scored - conceded;
    r.points -= scored > conceded ? 3 : scored === conceded ? 1 : 0;
  };
  for (const f of played) {
    if (dayOf(f) !== latest || f.is_forfeit === true) continue;
    const hs = Number(f.home_team_score);
    const as = Number(f.away_team_score);
    undo(f.home_team_id, hs, as);
    undo(f.away_team_id, as, hs);
  }

  const prevIndex = new Map([...before.values()].sort(compareStandingRows).map((r, i) => [String(r.id), i]));
  return rows.map((r, i) => ({ ...r, movement: prevIndex.get(String(r.id)) - i }));
}
